import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Plus, GripVertical, Users, Building2, Trash2, Target, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CRMToolsPanel } from "@/pages/crm/CRMToolsPanel";
import { ws, gbp } from "./api";
import { useWorkspace } from "./WorkspaceContext";
import { PageHeader, SubTabs, Panel, Empty, Loading, Field, NativeSelect } from "./ui";

const TABS = [
  { key: "pipeline", label: "Pipeline", to: "/crm-app/sales/pipeline" },
  { key: "leads", label: "Leads", to: "/crm-app/sales/leads" },
  { key: "contacts", label: "Contacts", to: "/crm-app/sales/contacts" },
  { key: "companies", label: "Companies", to: "/crm-app/sales/companies" },
  { key: "tools", label: "Import, Export & AI Coach", to: "/crm-app/sales/tools" },
];

const DealDialog = ({ open, onOpenChange, stages, contacts, onSaved }) => {
  const [f, setF] = useState({ name: "", company: "", value: "", stage: "lead", probability: 25, contact_id: null });
  const save = async () => {
    try {
      const c = contacts.find((x) => x.contact_id === f.contact_id);
      await ws("/crm/data/deals", { method: "POST", body: { ...f, value: Number(f.value) || 0, probability: Number(f.probability) || 0, contact_name: c?.name } });
      toast.success("Deal created"); onOpenChange(false); setF({ name: "", company: "", value: "", stage: "lead", probability: 25, contact_id: null }); onSaved();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} data-testid="deal-dialog">
        <DialogHeader><DialogTitle>New deal</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Field label="Deal name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="deal-name-input" /></Field></div>
          <Field label="Company"><Input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="deal-company-input" /></Field>
          <Field label="Value (£)"><Input type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="deal-value-input" /></Field>
          <Field label="Stage"><NativeSelect value={f.stage} onChange={(v) => setF({ ...f, stage: v || "lead" })} options={stages.map((s) => ({ value: s.id, label: s.name }))} placeholder="Stage" testid="deal-stage-select" /></Field>
          <Field label="Probability %"><Input type="number" value={f.probability} onChange={(e) => setF({ ...f, probability: e.target.value })} data-testid="deal-probability-input" /></Field>
          <div className="col-span-2"><Field label="Contact"><NativeSelect value={f.contact_id} onChange={(v) => setF({ ...f, contact_id: v })} options={contacts.map((c) => ({ value: c.contact_id, label: c.name }))} testid="deal-contact-select" /></Field></div>
        </div>
        <Button disabled={!f.name.trim()} onClick={save} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid="deal-save-btn">Create deal</Button>
      </DialogContent>
    </Dialog>
  );
};

const ContactDialog = ({ open, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", company: "", role: "" });
  const save = async () => {
    try {
      const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      await ws("/crm/data/contacts", { method: "POST", body });
      toast.success("Contact created"); onOpenChange(false); setF({ name: "", email: "", phone: "", company: "", role: "" }); onSaved();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} data-testid="contact-dialog">
        <DialogHeader><DialogTitle>New contact</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {[["name", "Full name"], ["email", "Email"], ["phone", "Phone"], ["company", "Company"], ["role", "Job title"]].map(([k, l]) => (
            <div key={k} className={k === "name" ? "col-span-2" : ""}><Field label={l}><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`contact-${k}-input`} /></Field></div>
          ))}
        </div>
        <Button disabled={!f.name.trim()} onClick={save} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid="contact-save-btn">Create contact</Button>
      </DialogContent>
    </Dialog>
  );
};

const Kanban = ({ stages, deals, setDeals, canDelete, reload }) => {
  const [drag, setDrag] = useState(null);
  const drop = async (stage) => {
    if (!drag || drag.stage === stage) return setDrag(null);
    setDeals((p) => p.map((d) => (d.deal_id === drag.deal_id ? { ...d, stage } : d)));
    try { await ws(`/crm/data/deals/${drag.deal_id}`, { method: "PATCH", body: { stage } }); toast.success(`Moved to ${stages.find((s) => s.id === stage)?.name}`); }
    catch (e) { toast.error(e.message); reload(); }
    setDrag(null);
  };
  const del = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try { await ws(`/crm/data/deals/${d.deal_id}`, { method: "DELETE" }); toast.success("Deal deleted"); reload(); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="overflow-x-auto pb-4 -mx-1">
      <div className="flex gap-3 min-w-max px-1">
        {stages.map((s) => {
          const list = deals.filter((d) => d.stage === s.id);
          return (
            <div key={s.id} className="w-72 shrink-0" onDragOver={(e) => e.preventDefault()} onDrop={() => drop(s.id)} data-testid={`kanban-column-${s.id}`}>
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="flex items-center gap-2 text-sm font-medium text-slate-800"><span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />{s.name}<span className="text-xs text-slate-400">{list.length}</span></span>
                <span className="text-xs text-slate-500">{gbp(list.reduce((a, d) => a + (d.value || 0), 0))}</span>
              </div>
              <div className="space-y-2 min-h-[320px] rounded-xl bg-slate-200/40 p-2">
                {list.map((d) => (
                  <div key={d.deal_id} draggable onDragStart={() => setDrag(d)}
                    className={`group bg-white rounded-lg border border-slate-200 p-3 cursor-grab active:cursor-grabbing hover:border-slate-300 transition-[border-color,opacity] ${drag?.deal_id === d.deal_id ? "opacity-50" : ""}`}
                    data-testid={`deal-card-${d.deal_id}`}>
                    <div className="flex justify-between gap-2"><p className="text-sm font-medium text-slate-900 leading-snug">{d.name}</p><GripVertical className="h-4 w-4 text-slate-300 shrink-0" /></div>
                    <p className="text-xs text-slate-500 mt-0.5">{d.company || "—"}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm font-semibold text-[#0B1530]">{gbp(d.value)}</span>
                      <span className="flex items-center gap-2 text-xs text-slate-400">{d.probability ?? 0}%
                        {canDelete && <button onClick={() => del(d)} className="opacity-0 group-hover:opacity-100 hover:text-red-600" data-testid={`deal-delete-${d.deal_id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                      </span>
                    </div>
                  </div>
                ))}
                {list.length === 0 && <p className="text-xs text-slate-400 text-center py-10">Drop deals here</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Contacts = ({ contacts, canDelete, reload }) => {
  const [q, setQ] = useState("");
  const list = contacts.filter((c) => [c.name, c.email, c.company].join(" ").toLowerCase().includes(q.toLowerCase()));
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await ws(`/crm/data/contacts/${c.contact_id}`, { method: "DELETE" }); toast.success("Contact deleted"); reload(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Panel testid="contacts-panel">
      <div className="relative mb-4 max-w-sm"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input className="pl-9" placeholder="Filter contacts" value={q} onChange={(e) => setQ(e.target.value)} data-testid="contacts-filter-input" /></div>
      {list.length === 0 ? <Empty icon={Users} title="No contacts" hint="Add a contact or import a CSV." /> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-2 pr-4 font-medium">Name</th><th className="pr-4 font-medium">Email</th><th className="pr-4 font-medium">Phone</th><th className="pr-4 font-medium">Company</th><th /></tr></thead>
          <tbody>{list.map((c) => (
            <tr key={c.contact_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`contact-row-${c.contact_id}`}>
              <td className="py-2.5 pr-4 font-medium text-slate-800">{c.name}<span className="block text-xs text-slate-400 font-normal">{c.role}</span></td>
              <td className="pr-4 text-slate-600">{c.email || "—"}</td><td className="pr-4 text-slate-600">{c.phone || "—"}</td><td className="pr-4 text-slate-600">{c.company || "—"}</td>
              <td className="text-right">{canDelete && <button onClick={() => del(c)} className="text-slate-400 hover:text-red-600" data-testid={`contact-delete-${c.contact_id}`}><Trash2 className="h-4 w-4" /></button>}</td>
            </tr>))}</tbody>
        </table></div>
      )}
    </Panel>
  );
};

const Companies = ({ contacts, deals }) => {
  const map = {};
  contacts.forEach((c) => { if (c.company) (map[c.company] ||= { contacts: 0, deals: 0, value: 0 }).contacts++; });
  deals.forEach((d) => { if (d.company) { const m = (map[d.company] ||= { contacts: 0, deals: 0, value: 0 }); m.deals++; m.value += d.value || 0; } });
  const rows = Object.entries(map).sort((a, b) => b[1].value - a[1].value);
  return (
    <Panel testid="companies-panel">
      {rows.length === 0 ? <Empty icon={Building2} title="No companies yet" hint="Companies appear when contacts or deals have a company name." /> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map(([name, m]) => (
            <div key={name} className="rounded-lg border border-slate-200 p-4" data-testid={`company-card-${name}`}>
              <p className="font-medium text-slate-900">{name}</p>
              <p className="text-xs text-slate-500 mt-1">{m.contacts} contacts · {m.deals} deals</p>
              <p className="text-sm font-semibold text-[#0B1530] mt-2">{gbp(m.value)}</p>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

export default function SalesPage() {
  const { tab = "pipeline" } = useParams();
  const [params, setParams] = useSearchParams();
  const { role } = useWorkspace();
  const [stages, setStages] = useState([]);
  const [deals, setDeals] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [dealOpen, setDealOpen] = useState(params.get("create") === "deal");
  const [contactOpen, setContactOpen] = useState(params.get("create") === "contact");
  const canDelete = role !== "employee";
  const load = async () => {
    try {
      const [s, d, c] = await Promise.all([ws("/crm/data/stages"), ws("/crm/data/deals"), ws("/crm/data/contacts")]);
      setStages(s.stages); setDeals(d.deals); setContacts(c.contacts);
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const c = params.get("create");
    if (c === "deal") setDealOpen(true);
    if (c === "contact") setContactOpen(true);
    if (c) setParams({}, { replace: true });
    // eslint-disable-next-line
  }, [params]);
  const seed = async () => {
    try { const r = await ws("/crm/data/seed-demo", { method: "POST" }); r.success ? toast.success("Sample data added") : toast.info("Workspace already has data"); load(); } catch (e) { toast.error(e.message); }
  };
  if (!deals) return <Loading />;
  const leads = deals.filter((d) => d.stage === "lead" || d.stage === "qualified");
  return (
    <div data-testid="sales-page">
      <PageHeader title="CRM & Sales" subtitle={`${deals.length} deals · ${contacts.length} contacts`} actions={<>
        <Button variant="outline" onClick={() => setContactOpen(true)} data-testid="new-contact-btn"><Plus className="h-4 w-4 mr-1" />Contact</Button>
        <Button onClick={() => setDealOpen(true)} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid="new-deal-btn"><Plus className="h-4 w-4 mr-1" />Deal</Button>
      </>} />
      <SubTabs tabs={TABS} />
      {tab === "pipeline" && (deals.length === 0
        ? <Panel><Empty icon={Target} title="Your pipeline is empty" hint="Create a deal, import a CSV, or load sample data to explore." action={<Button variant="outline" onClick={seed} data-testid="seed-demo-btn">Add sample data</Button>} /></Panel>
        : <Kanban stages={stages} deals={deals} setDeals={setDeals} canDelete={canDelete} reload={load} />)}
      {tab === "leads" && <Kanban stages={stages.filter((s) => ["lead", "qualified"].includes(s.id))} deals={leads} setDeals={setDeals} canDelete={canDelete} reload={load} />}
      {tab === "contacts" && <Contacts contacts={contacts} canDelete={canDelete} reload={load} />}
      {tab === "companies" && <Companies contacts={contacts} deals={deals} />}
      {tab === "tools" && (role === "employee"
        ? <Panel><Empty title="Bulk import and export are not available for your role" hint="Ask a Business Owner or Administrator to change your role." /></Panel>
        : <div className="-mx-4 sm:-mx-6 lg:-mx-10 rounded-xl overflow-hidden"><CRMToolsPanel deals={deals} onDataChanged={load} /></div>)}
      <DealDialog open={dealOpen} onOpenChange={setDealOpen} stages={stages} contacts={contacts} onSaved={load} />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} onSaved={load} />
    </div>
  );
}
